import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Menu,
  X,
  ShoppingBag,
  User,
  Flag,
  Info,
  LayoutGrid,
  PackageSearch,
  Notebook,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const navItems = [
    { name: "Beranda", href: "/", icon: LayoutGrid },
    { name: "Katalog", href: "/#catalog", icon: Notebook },
    { name: "Tracking", href: "/tracking-order", icon: PackageSearch },
    { name: "Tentang Kami", href: "/about", icon: Info },
  ];

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    const [path, hash] = href.split("#");

    // Jika kita berada di halaman yang sama dan ada hash
    if (hash && (path === location.pathname || path === "")) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        setIsMenuOpen(false); // Tutup menu mobile jika terbuka
      }
    } else {
      navigate(href);
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border shadow-soft">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-8 h-8 bg-gradient-independence rounded-lg flex items-center justify-center group-hover:animate-flag-wave">
              <Flag className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
              Bazaria<span className="text-accent"> Digital</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors font-medium"
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </a>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              asChild
              className="border-red-500 text-red-500 hover:bg-red-50"
            >
              <Link to="/auth">
                <User className="w-4 h-4" />
                Juragan
              </Link>
            </Button>
            <Button
              variant="independence"
              size="sm"
              onClick={() => (window.location.href = "/sell-product")}
            >
              <ShoppingBag className="w-4 h-4" />
              Titip Jual Produk
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={toggleMenu}
          >
            {isMenuOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-border animate-fade-in-up">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="flex items-center space-x-3 text-muted-foreground hover:text-primary transition-colors font-medium p-2 rounded-lg hover:bg-accent/10"
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </a>
                );
              })}

              <div className="flex flex-col space-y-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => {
                    setIsMenuOpen(false);
                    navigate("/auth");
                  }}
                >
                  <User className="w-4 h-4" />
                  Masuk Juragan
                </Button>
                <Button variant="independence" className="justify-start">
                  <ShoppingBag className="w-4 h-4" />
                  Mulai Belanja
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
